/**
 * Compass API client — the external code-&-schema impact-analysis service
 * (docs/flow/COMPASS.md). Same-origin via the nginx `/compass/*` proxy;
 * authenticated with the normal LISA session JWT (no extra secrets).
 * Read-only by construction on the server side.
 */
import { API_BASE, getAuthHeaders } from './apiClient';

/** Compass is reached through the LISA backend proxy (auth hardening
 *  Phase 2a): the browser never talks to Compass directly, so Compass holds
 *  no LISA user credential. Same JWT/session auth as every other API call. */
const BASE = `${API_BASE}/compass`;

export interface CompassEvidence {
  tool: string;
  input: Record<string, unknown>;
  /** Lookup category for grouping in the evidence accordion — one of
   *  "Graph search" | "Table" | "Field" | "Procedure" | "Method" |
   *  "Module" | "UI screen" | "Other". Absent on analyses stored before
   *  Compass added the field; treated as "Other". */
  category?: string;
}

export interface CompassEntity {
  kind: string;
  key: string;
  name: string;
  score: number | null;
}

/** One entry of the CR change plan (Compass /change/analyze contract,
 *  docs/flow/COMPASS_CR_IMPACT.md). impact_count is graph-measured or
 *  null — never invented. */
export interface ChangePlanItem {
  component: string;
  /** table | field | procedure | method | application | other */
  kind: string;
  /** modify | add | widen | test | verify | other */
  action: string;
  reason: string;
  impact_count: number | null;
  /** confirmed | likely | unverified */
  confidence: string;
}

export interface CompassAnalysis {
  /** Markdown with fixed sections: What's involved / Why this likely
   *  occurred / Impact if changed / Suggested next check. */
  explanation: string;
  /** Graph-lookup audit trail — every claim traces to one of these. */
  evidence: CompassEvidence[];
  /** Recognized entities (populated in degraded mode). */
  entities: CompassEntity[];
  model: string | null;
  /** true = AI unavailable/over budget; template-rendered graph facts. */
  degraded: boolean;
  elapsed_ms: number;
  /** CR mode only: structured change plan (null when degraded or if
   *  distillation failed — the markdown explanation is always primary). */
  change_plan?: ChangePlanItem[] | null;
  /** Support mode only: proposed data-correction script, REVIEW-ONLY by
   *  contract — Compass never executes; the UI must never offer to run it.
   *  Most tickets correctly return fixable_with_data_script: false. */
  data_fix?: CompassDataFix | null;
}

export interface CompassDataFixScripts {
  /** Table names the shared transaction touches (compass #72). */
  tables?: string[] | null;
  preview_sql?: string | null;
  backup_sql?: string | null;
  fix_sql?: string | null;
  rollback_sql?: string | null;
}

/** One per-table correction (compass message #67: `fix` became `fixes[]`,
 *  1+ entries, so multi-table corrections — master + detail tables — can be
 *  described). Field set beyond `table` is compass-owned; render defensively. */
export interface CompassDataFixItem {
  table?: string | null;
  description?: string | null;
}

export interface CompassDataFix {
  fixable_with_data_script: boolean;
  reason: string;
  /** Current shape (compass #67): array of per-table fixes. */
  fixes?: CompassDataFixItem[] | null;
  /** Pre-#67 singular shape — kept so older cached analyses still render. */
  fix?: { scripts?: CompassDataFixScripts | null } | null;
  /** Shared-transaction scripts across every table. */
  scripts?: CompassDataFixScripts | null;
}

/** Scripts are top-level per compass #72 (confirmed shape with sample);
 *  the `fix.scripts` leg covers analyses cached before the fixes[] rename. */
export function dataFixScripts(df: CompassDataFix): CompassDataFixScripts | null {
  return df.scripts ?? df.fix?.scripts ?? null;
}

export type CompassHint = string | { key: string; score?: number };

/** Serialize saved SQL Investigation cards into Compass's `data_context`
 *  field: raw observations only (title/SQL + result rows), never RCA
 *  conclusions. Per the Compass contract: `## <title>` per card, rows as
 *  text, `(0 rows returned)` for empties (highly diagnostic — always
 *  included), blank line between cards. Compass truncates at 8,000 chars
 *  keeping the FIRST cards, so data-bearing cards go first; cap 20,000. */
export function buildDataContext(
  cards:
    | {
        label?: string | null;
        sql?: string | null;
        rows?: Record<string, unknown>[] | null;
        rowCount?: number | null;
        error?: string | null;
      }[]
    | null
    | undefined,
): string {
  const MAX_CHARS = 20_000;
  if (!cards || cards.length === 0) return '';
  // Errored cards never ran — they are not observations. Null-tolerant:
  // restored/persisted cards can carry null rows or counts.
  const ran = cards
    .filter((c) => c && !c.error)
    .map((c) => {
      const rows = Array.isArray(c.rows) ? c.rows.filter((r) => r != null) : [];
      return {
        title: (c.label ?? '').trim() || (c.sql ?? '').trim().slice(0, 120),
        rows,
        count: typeof c.rowCount === 'number' ? c.rowCount : rows.length,
      };
    })
    .filter((c) => c.title);
  const ordered = [...ran.filter((c) => c.count > 0), ...ran.filter((c) => c.count === 0)];
  const sections: string[] = [];
  let used = 0;
  for (const c of ordered) {
    // A 0-row result is strong evidence for Compass — never mislabel a
    // data-bearing card whose row payload just wasn't persisted.
    const body =
      c.count === 0
        ? '(0 rows returned)'
        : c.rows.length === 0
          ? `(${c.count} rows returned; row data not available)`
          : c.rows.map((r) => JSON.stringify(r)).join('\n');
    const section = `## ${c.title}\n${body}`;
    if (used + section.length + 2 > MAX_CHARS) break;
    sections.push(section);
    used += section.length + 2;
  }
  return sections.join('\n\n');
}

/** Build Compass `hints` from the saved SQL cards: distinct table FQNs,
 *  data-bearing cards first (order = rank per the Compass contract),
 *  capped at 8 (precision beats recall — server caps at 20). Keys are
 *  the live Neo4j FQNs verbatim, which is exactly what Compass expects;
 *  degraded mode uses them too, so always send. */
export function buildHints(
  cards:
    | { sql?: string | null; rowCount?: number | null; error?: string | null }[]
    | null
    | undefined,
  modules?: string[] | null,
): CompassHint[] {
  const fqnRe = /\b(?:from|join)\s+([A-Za-z0-9_\[\]]+\.[A-Za-z0-9_\[\]]+\.[A-Za-z0-9_\[\]]+)/gi;
  const seen = new Set<string>();
  const hints: string[] = [];
  const ran = (cards ?? []).filter((c) => c && !c.error && c.sql);
  const ordered = [
    ...ran.filter((c) => (c.rowCount ?? 0) > 0),
    ...ran.filter((c) => (c.rowCount ?? 0) === 0),
  ];
  for (const c of ordered) {
    fqnRe.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = fqnRe.exec(c.sql as string)) !== null) {
      const fqn = m[1].replace(/[\[\]]/g, '');
      const key = fqn.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        hints.push(fqn);
        if (hints.length >= 8) break;
      }
    }
    if (hints.length >= 8) break;
  }
  // Detected modules are graph-key candidates too — after the table FQNs,
  // which are harder evidence (they come from queries that actually ran).
  for (const m of modules ?? []) {
    const key = m.trim().toLowerCase();
    if (key && !seen.has(key)) {
      seen.add(key);
      hints.push(m.trim());
      if (hints.length >= 14) break;
    }
  }
  return hints;
}

/** Primary ticket text for Compass `text`: the reporter's own words plus
 *  extracted entities — never the LLM condensed summary (generated text must
 *  not re-enter a model call). Capped 20K chars, Compass's limit. */
export function buildTicketText(st: {
  summary?: string | null;
  description?: string | null;
  steps_to_reproduce?: string | null;
  comments?: { author: string; body: string }[] | null;
  entities?: { entity_type: string; value: string }[] | null;
}): string {
  const parts: string[] = [];
  if (st.summary?.trim()) parts.push(st.summary.trim());
  if (st.description?.trim()) parts.push(st.description.trim());
  if (st.steps_to_reproduce?.trim()) parts.push(`Steps to reproduce:\n${st.steps_to_reproduce.trim()}`);
  const entities = (st.entities ?? []).filter((e) => e.entity_type && e.value);
  if (entities.length) {
    parts.push(`Extracted entities: ${entities.map((e) => `${e.entity_type}=${e.value}`).join(', ')}`);
  }
  for (const c of st.comments ?? []) {
    if (c.body?.trim()) parts.push(`Comment (${c.author}): ${c.body.trim()}`);
  }
  return parts.join('\n\n').slice(0, 20_000);
}

/** data_context = SQL cards first (hardest evidence — Compass keeps the head
 *  on truncation), then screenshot OCR and attachment-document extracts as
 *  labelled primary-evidence blocks. Total cap 20K, Compass's limit. */
export function buildDataContextWithEvidence(
  cards: Parameters<typeof buildDataContext>[0],
  imageText?: string | null,
  documentText?: string | null,
  userDocs?: { doc_title: string; section_hierarchy?: string[]; content: string; score?: number }[] | null,
): string {
  const CAP = 20_000;
  const parts = [buildDataContext(cards)];
  if (imageText?.trim()) parts.push(`## Screenshot text (OCR extract)\n${imageText.trim()}`);
  let out = parts.filter(Boolean).join('\n\n').slice(0, CAP);

  // Document blocks are the truncation buffer (Anthony 2026-08-19): SQL rows
  // and OCR keep priority; docs shrink to whatever budget remains.
  const append = (block: string) => {
    const budget = CAP - out.length - 40;
    if (budget < 200) return;
    out += `\n\n${block.length > budget ? `${block.slice(0, budget)}\n…[truncated]` : block}`;
  };
  if (documentText?.trim()) append(`## Attachment documents (extract)\n${documentText.trim()}`);
  const docs = (userDocs ?? []).filter((d) => d.content?.trim());
  if (docs.length) {
    const sorted = [...docs].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    append(
      '## User documentation (extracts)\n' +
        sorted
          .map((d) => `### ${d.doc_title}${d.section_hierarchy?.length ? ` — ${d.section_hierarchy.join(' > ')}` : ''}\n${d.content.trim()}`)
          .join('\n\n'),
    );
  }
  return out;
}

/** AI analysis of a support issue against the LRP code + schema graph.
 *  Slow (20–90s) and billed — call only on explicit user action.
 *  `dataContext` (optional): serialized SQL evidence via buildDataContext —
 *  upgrades Compass from ranked hypotheses to confirmed/ruled-out verdicts. */
export async function analyzeIssue(
  text: string,
  dataContext?: string,
  hints?: CompassHint[],
): Promise<CompassAnalysis> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 120_000);
  try {
    const res = await fetch(`${BASE}/support/analyze`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({
        text,
        hints,
        data_context: dataContext && dataContext.trim() ? dataContext : undefined,
      }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`Compass ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

export interface ChangeGraphNode {
  id: string;
  type: string;
  name: string;
  description?: string;
}
export interface ChangeGraphEdge {
  source: string;
  target: string;
  type: string;
  /** true = related only through a shared table (type "RELATED");
   *  absent on direct edges (thread change-graph #51). */
  indirect?: boolean;
  /** Ready-to-display explanation of the indirect path. */
  description?: string;
}
export interface ChangeGraph {
  nodes: ChangeGraphNode[];
  edges: ChangeGraphEdge[];
}

/** Impact subgraph for a change plan. Direct graph read on Compass — no AI
 *  cost, seconds. Auto-loaded when a completed analysis is on screen (no
 *  click gating — Anthony explicit). Entities of kind application/other may
 *  come back as isolated nodes; that is expected, not an error. */
export async function fetchChangeGraph(changePlan: ChangePlanItem[]): Promise<ChangeGraph> {
  const res = await fetch(`${BASE}/change/graph`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ change_plan: changePlan }),
  });
  if (!res.ok) throw new Error(`Compass ${res.status}`);
  return res.json();
}

/** Instant deterministic drill-downs (no AI call, no cost). */
async function get(path: string): Promise<{ explanation: string }> {
  const res = await fetch(`${BASE}${path}`, { headers: await getAuthHeaders() });
  if (!res.ok) throw new Error(`Compass ${res.status}`);
  return res.json();
}
export const explainTable = (key: string) => get(`/explain/table/${encodeURIComponent(key)}`);
export const explainField = (key: string) => get(`/explain/field/${encodeURIComponent(key)}`);
export const explainProc = (key: string) => get(`/explain/proc/${encodeURIComponent(key)}`);

/** CR impact analysis (developer-grade, deeper tool loop). Contract:
 *  docs/flow/COMPASS_CR_IMPACT.md — text ≤200K chars, typical runs 2–4 min
 *  (nginx allows 300s), rate 1/min. Paid call: only on explicit user
 *  action, persist the result LISA-side. */
export async function analyzeChange(
  text: string,
  hints?: CompassHint[],
  dataContext?: string,
): Promise<CompassAnalysis> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 300_000);
  try {
    const res = await fetch(`${BASE}/change/analyze`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({
        text,
        hints,
        data_context: dataContext && dataContext.trim() ? dataContext : undefined,
      }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`Compass ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

// ── Submit-and-poll jobs ───────────────────────────────────────────────
// A held-open connection made every timeout in the chain a way to lose paid
// work (a 122s analysis completed, the connection was gone, $0.78 wasted).
// Submit returns instantly; the backend runs and persists the analysis, and
// a second submit for the same ticket joins the in-flight run instead of
// paying twice.

export interface CompassJob {
  status: 'running' | 'done' | 'failed' | 'none';
  response?: CompassAnalysis;
  error?: string;
  job_id?: string;
}

export async function submitAnalysis(
  mode: 'change' | 'support',
  key: string,
  text: string,
  opts?: { hints?: CompassHint[]; dataContext?: string },
): Promise<{ job_id: string; deduped: boolean }> {
  const res = await fetch(`${BASE}/jobs`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({
      mode,
      key,
      text,
      hints: opts?.hints,
      data_context: opts?.dataContext?.trim() ? opts.dataContext : undefined,
    }),
  });
  if (!res.ok) throw new Error(`Compass ${res.status}`);
  return res.json();
}

export async function getJob(mode: 'change' | 'support', key: string): Promise<CompassJob> {
  const res = await fetch(`${BASE}/jobs/${mode}/${encodeURIComponent(key)}`, {
    headers: await getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Compass ${res.status}`);
  return res.json();
}

/** Poll until the job leaves `running`. Resolves with the finished job. */
export async function pollJob(
  mode: 'change' | 'support',
  key: string,
  opts?: { intervalMs?: number; timeoutMs?: number; onTick?: (secs: number) => void },
): Promise<CompassJob> {
  const interval = opts?.intervalMs ?? 4000;
  const deadline = Date.now() + (opts?.timeoutMs ?? 420_000);
  const started = Date.now();
  for (;;) {
    const job = await getJob(mode, key);
    if (job.status !== 'running') return job;
    // "We stopped waiting" — NOT "Compass failed". The analysis is very
    // likely still running and will finish; naming the service as the
    // failure sends the next person debugging in the wrong direction.
    if (Date.now() > deadline) {
      return { status: 'failed', error: 'CLIENT_GAVE_UP' };
    }
    opts?.onTick?.(Math.round((Date.now() - started) / 1000));
    await new Promise((r) => setTimeout(r, interval));
  }
}

// ── Persistence (LISA backend, migration 033) ──────────────────────────
// One paid Compass run per analysis ever: the first success is stored on
// the analysis row; later viewers load it instantly. Re-run overwrites.

export async function getSavedAnalysis(
  analysisId: number,
): Promise<{ response: CompassAnalysis | null; saved_at: string | null }> {
  const res = await fetch(`${API_BASE}/issue-analysis/${analysisId}/compass`, {
    headers: await getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Compass store ${res.status}`);
  return res.json();
}

export async function saveAnalysis(
  analysisId: number,
  analysis: CompassAnalysis,
): Promise<void> {
  const res = await fetch(`${API_BASE}/issue-analysis/${analysisId}/compass`, {
    method: 'PUT',
    headers: await getAuthHeaders(),
    body: JSON.stringify(analysis),
  });
  if (!res.ok) throw new Error(`Compass store ${res.status}`);
}

/** Result of an IAD generation attempt: either the file or a readable reason. */
export type IadResult =
  | { ok: true; blob: Blob; filename: string }
  | { ok: false; message: string };

/** Fill the IAD Word template from an analysis already on screen.
 *
 *  Goes through the LISA proxy like every other Compass call — the service
 *  token stays server-side. Errors come back as JSON even from a download
 *  endpoint, so the reason is read rather than saved as a broken file. */
export async function generateIad(
  text: string,
  explanation: string,
  changePlan: ChangePlanItem[],
): Promise<IadResult> {
  const res = await fetch(`${BASE}/change/generate-iad`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ text, explanation, change_plan: changePlan }),
  });

  if (!res.ok) {
    if (res.status === 429) {
      return { ok: false, message: 'Rate limited — three documents per two minutes. Try again shortly.' };
    }
    const body = await res.text();
    let message = body;
    try {
      const j = JSON.parse(body);
      message = j.detail || j.message || j.error || body;
    } catch {
      /* not JSON — show the raw text */
    }
    return { ok: false, message: message.slice(0, 300) || `Generation failed (${res.status})` };
  }

  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return {
    ok: true,
    blob: await res.blob(),
    filename: match?.[1]?.trim() || 'IAD.docx',
  };
}

/** Search & Browse — real Compass responses:
 *  exports/lisa-search-browse-2026-09-26 (Compass repo, commit 0fb0435). */
export type SearchKind = 'method' | 'procedure' | 'table' | 'field' | 'xhtml' | 'vo' | 'ctrl';

export const SEARCH_KINDS: { kind: SearchKind; label: string }[] = [
  { kind: 'method', label: 'Methods' },
  { kind: 'procedure', label: 'Stored procedures' },
  { kind: 'table', label: 'Tables' },
  { kind: 'field', label: 'Fields' },
  { kind: 'xhtml', label: 'XHTML screens' },
  { kind: 'vo', label: 'Value objects (VO)' },
  { kind: 'ctrl', label: 'Controllers (Ctrl)' },
];

export interface SearchItem {
  kind?: string;
  /** Unique key — name is not unique in this estate; source lookups use this. */
  key: string;
  name: string;
  app?: string | null;
  score?: number;
  has_table_edge?: boolean;
  has_proc_edge?: boolean;
  /** Compass-written wording; shown verbatim. */
  edge_note?: string | null;
  has_text?: boolean;
  /** Path recorded on the node — a label, not an authority: source lookup
   *  may resolve a different copy (see MethodSource.resolved_note). */
  file?: string;
  line_start?: number | null;
  line_end?: number | null;
}

export interface KindCount {
  total: number;
  returned: number;
  missing: number;
  complete: boolean;
}

export interface SearchResponse {
  query?: string;
  /** How Compass split the query into AND-ed words. */
  terms?: string[];
  counts: Record<string, KindCount>;
  results: Record<string, SearchItem[]>;
}

export interface BrowseItem {
  value: string;
  count?: number;
  tables?: number;
  procedures?: number;
  key?: string;
}

/** Module → App → Project → Class → Method. */
export interface BrowseSelection {
  module?: string | null;
  app?: string | null;
  project?: string | null;
  class?: string | null;
}

export interface BrowseResponse {
  level: string;
  items: BrowseItem[];
  counts?: { items?: KindCount };
  /** Compass's caveat on what the chain table does and does not cover. */
  note?: string;
}

export interface MethodSource {
  content?: string;
  file?: string;
  start_line?: number;
  end_line?: number;
  truncated?: boolean;
  /** Set when the text came from a different file copy than the key names —
   *  must be shown to the user, not swallowed. */
  resolved_note?: string | null;
  error?: string;
}

async function compassJson<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`Compass ${res.status}`);
  return res.json();
}

export async function searchNames(
  q: string,
  opts: { kinds?: SearchKind[]; perKind?: number; app?: string | null } = {},
): Promise<SearchResponse> {
  const params = new URLSearchParams({ q });
  params.set('kinds', (opts.kinds ?? SEARCH_KINDS.map((k) => k.kind)).join(','));
  params.set('per_kind', String(opts.perKind ?? 8));
  if (opts.app) params.set('app', opts.app);
  const res = await fetch(`${BASE}/search?${params.toString()}`, { headers: await getAuthHeaders() });
  return compassJson<SearchResponse>(res);
}

export async function browseChain(sel: BrowseSelection): Promise<BrowseResponse> {
  const params = new URLSearchParams();
  if (sel.module) params.set('module', sel.module);
  if (sel.app) params.set('app', sel.app);
  if (sel.project) params.set('project', sel.project);
  if (sel.class) params.set('class', sel.class);
  const res = await fetch(`${BASE}/browse?${params.toString()}`, { headers: await getAuthHeaders() });
  return compassJson<BrowseResponse>(res);
}

export interface ProcSource {
  key?: string;
  content?: string;
  truncated?: boolean;
  real_chars?: number;
  declared_schema?: string;
}

/** Errors carry Compass's own message (e.g. "belongs to schema X, not Y"). */
export async function getProcSource(key: string): Promise<ProcSource> {
  const res = await fetch(`${BASE}/proc-source/${encodeURIComponent(key)}`, {
    headers: await getAuthHeaders(),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = typeof body?.detail === 'string' ? body.detail : typeof body?.error === 'string' ? body.error : '';
    } catch { /* body was not JSON */ }
    throw new Error(detail || `Compass ${res.status}`);
  }
  return res.json();
}

export async function getMethodSource(key: string): Promise<MethodSource> {
  const res = await fetch(`${BASE}/method-source?${new URLSearchParams({ key }).toString()}`, {
    headers: await getAuthHeaders(),
  });
  return compassJson<MethodSource>(res);
}

// ── Procedure and method maps ─────────────────────────────────────────────
// Compass turns an unreadably large procedure or method into a short map of
// nodes, then opens one node at a time. Every field is optional: the panel
// shows what arrives and never invents what does not.

export interface MapCounts {
  total?: number;
  returned?: number;
  missing?: number;
  complete?: boolean;
}

export interface MapCondition {
  text?: string;
  line?: number;
  /** Compass labels it "nearest preceding condition" — NOT a proven guard. */
  why?: string;
}

export interface MapNode {
  id: string;
  kind?: string;
  start_line?: number;
  end_line?: number;
  head?: string;
  touches?: string[];
  folded_lines?: number;
  condition?: MapCondition | string | null;
  why?: string;
}

export interface MapResponse {
  /** "whole_body" or "map" — never both in one response. Compass's current
   *  tools call it `size_tier` ("map", "map_touch_required"); both are read. */
  tier?: string;
  size_tier?: string;
  /** false = Compass declined to map (short method, unparsed procedure). */
  mapped?: boolean;
  reason?: string;
  /** The tool to use instead, e.g. "get_method_source". */
  read_instead?: string;
  total_lines?: number;
  total_chars?: number;
  touch?: string | string[] | null;
  filtered_by_touch?: boolean;
  body_chars?: number;
  content?: string;
  nodes?: MapNode[];
  counts?: { nodes?: MapCounts; lines?: MapCounts };
  folded?: unknown;
  /** Both counts (map vs raw source) per kind, not just a verdict word. */
  completeness?: Record<string, unknown>;
  note?: string;
  warning?: string;
  body_unavailable?: string;
  opened?: NodeResponse[];
  [extra: string]: unknown;
}

export interface NodeLine {
  line_number: number;
  content: string;
  commented?: boolean;
}

export interface NodeResponse {
  node_id?: string;
  start_line?: number;
  end_line?: number;
  lines?: NodeLine[];
  counts?: { lines?: MapCounts };
  next_from_line?: number | null;
  commented_out_lines?: number;
  commented_line_numbers?: number[];
  comment_warning?: string;
  note?: string;
  warning?: string;
}

async function postMap<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const b = await res.json();
      detail = typeof b?.detail === 'string' ? b.detail : typeof b?.error === 'string' ? b.error : '';
    } catch { /* body was not JSON */ }
    throw new Error(detail || (res.status === 404 ? 'Compass has not published this endpoint yet' : `Compass ${res.status}`));
  }
  return res.json();
}

export interface MapOptions {
  touch?: string[];
  maxNodes?: number;
  open?: string[];
}

const mapBody = (key: string, o: MapOptions) => ({
  key,
  touch: o.touch ?? [],
  ...(o.maxNodes ? { max_nodes: o.maxNodes } : {}),
  open: o.open ?? [],
});

/** One tiered call: the whole body when small, otherwise the map. */
export function readProc(key: string, o: MapOptions = {}): Promise<MapResponse> {
  return postMap<MapResponse>('/proc/read', mapBody(key, o));
}

export function openProcNode(
  key: string,
  nodeId: string,
  o: { maxChars?: number; fromLine?: number } = {},
): Promise<NodeResponse> {
  return postMap<NodeResponse>('/proc/node', {
    key,
    node_id: nodeId,
    ...(o.maxChars ? { max_chars: o.maxChars } : {}),
    ...(o.fromLine ? { from_line: o.fromLine } : {}),
  });
}

export function mapMethod(key: string, o: MapOptions = {}): Promise<MapResponse> {
  return postMap<MapResponse>('/method/map', mapBody(key, o));
}
